import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Constants, PopupType } from 'src/app/core/models/common.models';
import { EventType, FavoriteEventAction, IPopupInputForm, IReviewVote, VoteState } from '../../models/passport.models';
import { Profile, Stamp } from '../../models/profile.model';

@Component({
  selector: 'app-popup',
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.css']
})
export class PopupComponent implements OnInit, OnChanges, AfterViewInit {

  private static readonly DEFAULT_EVENT_RATING = 4;

  private readonly POPUP_DICTIONARY = {
    [PopupType.EVENT_REVIEW]: {
      popupId: 'reviewPopup',
      popupClass: 'dark-modal',
      formGroup: new FormGroup({
        review: new FormControl('', Validators.required),
        rating: new FormControl(4, Validators.required)
      })
    }
  };

  @ViewChild('reviewPopup') reviewPopup: TemplateRef<Component>;

  @Input() popupType: PopupType;
  @Input() public popupData: Stamp;
  @Input() eventType: EventType;
  @Input() readonly: boolean;

  public contentTitle = '';
  public content = '';
  public shortContent = '';
  public upvoteCount = 0;
  public downvoteCount = 0;
  public upvoteExists = false;
  public downvoteExists = false;
  public eventPurchaseTime = '';
  public favorite = false;
  public contentType: 'DESCRIPTION' | 'REVIEW';
  public showReviewForm = false;
  public votesMessage = 'Community Feedback';

  public eventReviewForm: FormGroup;

  private modalRef: NgbModalRef;

  private passportProfile: Profile;

  @Output() closeEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() popupInputSubmit: EventEmitter<IPopupInputForm> = new EventEmitter();

  constructor(private modalService: NgbModal) { }

  ngOnInit(): void {
    this.eventReviewForm = new FormGroup({
      review: new FormControl(''),
      rating: new FormControl(PopupComponent.DEFAULT_EVENT_RATING),
      upvote: new FormControl(0)
    });

    this.passportProfile = JSON.parse(localStorage.getItem(Constants.localStorageKeys.PASSPORT_PROFILE)) as Profile;

    if (this.popupData.review) {
      this.contentTitle = 'Thoughts';
      this.content = this.popupData.review.review;
      this.contentType = 'REVIEW';

      this.getFormGroup().setControl('review', new FormControl(this.content));
      this.getFormGroup().setControl('rating', new FormControl(this.popupData.review.rating));

      if (this.readonly) {
        this.votesMessage = 'Do you agree?';
        this.getFormGroup().controls.review.disable({onlySelf: true});
        this.getFormGroup().controls.rating.disable({onlySelf: true});
      }

      this.upvoteCount = this.popupData.review.votes.filter((vote: IReviewVote) => vote.voteState === VoteState.UPVOTE)?.length;
      this.downvoteCount = this.popupData.review.votes.filter((vote: IReviewVote) => vote.voteState === VoteState.DOWNVOTE)?.length;

      this.downvoteExists = !!this.popupData.review.votes
      .find((vote: IReviewVote) => vote.voteState === VoteState.DOWNVOTE && vote.userId === this.passportProfile.userId);
      this.upvoteExists = !!this.popupData.review.votes
        .find((vote: IReviewVote) => vote.voteState === VoteState.UPVOTE && vote.userId === this.passportProfile.userId);
    } else {
      this.contentTitle = 'Short Description';
      this.content = this.popupData.shortDescription;
      this.contentType = 'DESCRIPTION';
    }

    this.shortContent = this.content.slice(0, 100);
    this.favorite = this.popupData.favorite;
    this.eventPurchaseTime = new Date(this.popupData.purchaseTime).getTime() > 0 ? new Date(this.popupData.purchaseTime).toDateString() : '--';
  }

  ngOnChanges(): void {
    this.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.modalRef = this.modalService.open(this[this.POPUP_DICTIONARY[this.popupType].popupId], {
      modalDialogClass: 'modal-dialog',
      windowClass: 'modal-window',
      centered: true,
      scrollable: true,
      size: 'xl'
    });

    this.modalRef.result.then(() => {
      this.closeEvent.emit(true);
    }, () => {
      this.closeEvent.emit(true);
    });
  }

  public toggleContentSize(): void {
    if (this.shortContent !== this.content) {
      this.shortContent = this.content;
    } else {
      this.shortContent = this.content.slice(0, 100);
    }
  }

  public toggleReviewForm(): void {
    this.showReviewForm = !this.showReviewForm;
    if (!this.showReviewForm) {
      this.getFormGroup().setControl(
        'review',
        this.popupData.review
          ? new FormControl(this.content)
          : new FormControl('')
      );
      this.getFormGroup().setControl(
        'rating',
        this.popupData.review
          ? new FormControl(this.popupData.review.rating)
          : new FormControl(PopupComponent.DEFAULT_EVENT_RATING)
      );
    }
  }

  public enableReviewEdit(): void {
    this.showReviewForm = true;
  }

  public getFormGroup(): FormGroup {
    return this.POPUP_DICTIONARY[this.popupType].formGroup;
  }

  public publishPopupData(): void {
    this.popupInputSubmit.emit({
      popupType: this.popupType,
      popupForm: {
        ...this.POPUP_DICTIONARY[this.popupType].formGroup.value,
        ...{
          eventReviewId: this.popupData.review ? this.popupData.review.id : null,
          eventId: this.popupData.eventId,
          eventType: this.eventType
        }
      }
    });
    this.modalRef.close(); // this closes the modal - the event handler will instruct to open it again
  }

  public getVoteStateCSS(voteState: string): string {
    if (this.upvoteExists || this.downvoteExists) {
      return voteState === VoteState.UPVOTE ? 'user-upvote' : 'user-downvote';
    }
    return '';
  }

  public toggleUpvote(): void {
    if (this.popupData.review.userId === this.passportProfile.userId) {
      console.warn('A User cannot vote on their own review');
      return;
    }

    let voteState = VoteState.UPVOTE;
    if (this.upvoteExists) {
      voteState = VoteState.NEUTRAL;
    }

    this.popupInputSubmit.emit({
      popupType: this.popupType,
      popupForm: {
        eventReviewId: this.popupData.review.id,
        voteState
      }
    });
    this.modalRef.close();
  }

  public toggleDownvote(): void {
    if (this.popupData.review.userId === this.passportProfile.userId) {
      console.warn('A User cannot vote on their own review');
      return;
    }

    let voteState = VoteState.DOWNVOTE;
    if (this.downvoteExists) {
      voteState = VoteState.NEUTRAL;
    }

    this.popupInputSubmit.emit({
      popupType: this.popupType,
      popupForm: {
        eventReviewId: this.popupData.review.id,
        voteState
      }
    });
    this.modalRef.close();
  }

  public toggleFavoriteEventState(): void {
    this.popupInputSubmit.emit({
      popupType: this.popupType,
      popupForm: {
        eventId: this.popupData.eventId,
        favoriteState: this.favorite ? FavoriteEventAction.UNSET : FavoriteEventAction.SET // toggling the favorite state
      }
    });
    this.modalRef.close();
  }
}
